import React, { useState } from 'react';
import { contacts, deals, tasks } from '../../data/mockData';

const AVATAR_COLORS = [
  { bg:'rgba(167,244,50,0.15)',  color:'#A7F432' },
  { bg:'rgba(79,195,247,0.15)',  color:'#4fc3f7' },
  { bg:'rgba(245,166,35,0.15)',  color:'#f5a623' },
  { bg:'rgba(255,77,77,0.15)',   color:'#ff4d4d' },
  { bg:'rgba(138,154,151,0.15)', color:'#8a9a97' },
];
const avatarColor = id => AVATAR_COLORS[id % AVATAR_COLORS.length];
const initials = c => `${c.firstName?.[0] || ''}${c.lastName?.[0] || ''}`;

const statusBadge = s => ({
  Activated:    'badge badge-green',
  'In Progress':'badge badge-amber',
  'Not Started':'badge badge-gray',
})[s] || 'badge badge-gray';

const typeBadge = t => t === 'Club' ? 'badge badge-amber' : 'badge badge-blue';

const activityIcon = type => ({
  note:  '📝',
  email: '✉️',
  call:  '📞',
  task:  '✅',
  stage: '🔄',
  deal:  '💼',
})[type] || '•';

export default function ContactRecord({ contactId, onBack, toast }) {
  const contact = contacts.find(c => c.id === contactId) || contacts[0];
  const contactDeals = deals.filter(d => d.contactId === contact.id);
  const contactTasks = tasks.filter(t => t.contact === `${contact.firstName} ${contact.lastName}`);

  const [activities, setActivities] = useState([
    { id:1, type:'note',  text:`Contact imported from ${contact.source}`, date:contact.created, actor:'System' },
    { id:2, type:'email', text:'Welcome email sent automatically', date:contact.created, actor:'System' },
    { id:3, type:'stage', text:`Status set to ${contact.status}`, date:contact.created, actor:'System' },
  ]);

  const [note, setNote]     = useState('');
  const [editMode, setEditMode] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [editData, setEditData] = useState({ ...contact });
  const [showLog, setShowLog] = useState(false);

  const logNote = () => {
    if (!note.trim()) return;
    const a = {
      id: Date.now(), type: 'note', text: note,
      date: new Date().toLocaleString('en-GB', { day:'numeric', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' }),
      actor: 'Habeeb H.',
    };
    setActivities(prev => [a, ...prev]);
    setNote(''); setShowLog(false);
    toast?.('Note logged');
  };

  const ac = avatarColor(contact.id);

  return (
    <div>
      {/* Back button */}
      <button
        className="btn btn-ghost"
        style={{ marginBottom: 16, fontSize: 13 }}
        onClick={onBack}
      >
        ← Back to contacts
      </button>

      {/* Header card */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
          <div
            className="avatar"
            style={{ width: 56, height: 56, fontSize: 18, background: ac.bg, color: ac.color, flexShrink: 0 }}
          >
            {initials(contact)}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 4 }}>
              <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 22, fontWeight: 600, color: 'var(--text-primary)' }}>
                {contact.firstName} {contact.lastName}
              </h2>
              <span className={typeBadge(contact.type)}>{contact.type}</span>
              <span className={statusBadge(contact.status)}>{contact.status}</span>
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 2 }}>{contact.job} · {contact.company}</div>
            <div style={{ fontSize: 12, color: 'var(--text-dim)' }}>{contact.country} · Last activity: {contact.lastActivity}</div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            <button className="btn btn-primary" style={{ fontSize: 12 }} onClick={() => toast?.('Email composer opening...')}>
              ✉ Send email
            </button>
            <button className="btn btn-ghost" style={{ fontSize: 12 }} onClick={() => setEditMode(v => !v)}>
              {editMode ? 'Cancel' : '✎ Edit'}
            </button>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 20, alignItems: 'start' }}>
        {/* Left column */}
        <div>
          {/* Tabs */}
          <div style={{ display: 'flex', gap: 4, marginBottom: 16, borderBottom: '1px solid var(--border)', paddingBottom: 0 }}>
            {['overview', 'deals', 'tasks', 'activity'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  padding: '8px 16px', fontSize: 13, fontFamily: 'var(--font-ui)',
                  color: activeTab === tab ? 'var(--accent)' : 'var(--text-muted)',
                  borderBottom: activeTab === tab ? '2px solid var(--accent)' : '2px solid transparent',
                  marginBottom: -1, textTransform: 'capitalize', transition: 'all 0.15s',
                }}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* Overview tab */}
          {activeTab === 'overview' && (
            <div className="card">
              <div className="card-header" style={{ marginBottom: 16 }}>
                <span className="card-title">Contact details</span>
                {editMode && (
                  <button className="btn btn-primary" style={{ fontSize: 12 }} onClick={() => { setEditMode(false); toast?.('Contact updated'); }}>
                    Save changes
                  </button>
                )}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                {[
                  ['First name', 'firstName'],
                  ['Last name', 'lastName'],
                  ['Email', 'email'],
                  ['Phone', 'phone'],
                  ['Company', 'company'],
                  ['Job title', 'job'],
                  ['Country', 'country'],
                  ['Lead source', 'source'],
                ].map(([label, key]) => (
                  <div key={key}>
                    <div style={{ fontSize: 11, color: 'var(--text-dim)', fontFamily: 'var(--font-ui)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 4 }}>{label}</div>
                    {editMode ? (
                      <input
                        className="search-input"
                        style={{ width: '100%' }}
                        value={editData[key] || ''}
                        onChange={e => setEditData(prev => ({ ...prev, [key]: e.target.value }))}
                      />
                    ) : (
                      <div style={{ fontSize: 13, color: 'var(--text-primary)' }}>{contact[key] || '—'}</div>
                    )}
                  </div>
                ))}
              </div>
              {contact.notes && (
                <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
                  <div style={{ fontSize: 11, color: 'var(--text-dim)', fontFamily: 'var(--font-ui)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 6 }}>Notes</div>
                  <div style={{ fontSize: 13, color: 'var(--text-muted)', lineHeight: 1.6 }}>{contact.notes}</div>
                </div>
              )}
            </div>
          )}

          {/* Deals tab */}
          {activeTab === 'deals' && (
            <div className="card">
              <div className="card-header" style={{ marginBottom: 16 }}>
                <span className="card-title">Deals ({contactDeals.length})</span>
                <button className="btn btn-primary" style={{ fontSize: 12 }} onClick={() => toast?.('Create deal modal coming soon')}>+ New deal</button>
              </div>
              {contactDeals.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--text-dim)' }}>No deals yet</div>
              ) : contactDeals.map(deal => (
                <div key={deal.id} style={{ padding: '14px 0', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontWeight: 500, fontSize: 13, color: 'var(--text-primary)', marginBottom: 3 }}>{deal.name}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-dim)' }}>{deal.pipeline} · Closes {deal.closeDate}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontFamily: 'var(--font-display)', fontSize: 16, color: 'var(--accent)', fontWeight: 600 }}>${deal.amount}</div>
                    <span className="badge badge-green" style={{ fontSize: 10 }}>{deal.stage}</span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Tasks tab */}
          {activeTab === 'tasks' && (
            <div className="card">
              <div className="card-header" style={{ marginBottom: 16 }}>
                <span className="card-title">Tasks ({contactTasks.length})</span>
                <button className="btn btn-primary" style={{ fontSize: 12 }} onClick={() => toast?.('Task created')}>+ New task</button>
              </div>
              {contactTasks.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--text-dim)' }}>No tasks for this contact</div>
              ) : contactTasks.map(task => (
                <div key={task.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: '1px solid var(--border)' }}>
                  <div style={{ width: 16, height: 16, borderRadius: '50%', border: `2px solid ${task.done ? 'var(--accent)' : 'var(--border)'}`, background: task.done ? 'var(--accent)' : 'transparent', flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, color: task.done ? 'var(--text-dim)' : 'var(--text-primary)', textDecoration: task.done ? 'line-through' : 'none' }}>{task.title}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-dim)', marginTop: 2 }}>{task.workflow} · Due: {task.due}</div>
                  </div>
                  <span className={`badge ${task.priority === 'High' ? 'badge-red' : task.priority === 'Medium' ? 'badge-amber' : 'badge-gray'}`} style={{ fontSize: 10 }}>{task.priority}</span>
                </div>
              ))}
            </div>
          )}

          {/* Activity tab */}
          {activeTab === 'activity' && (
            <div className="card">
              <div className="card-header" style={{ marginBottom: 16 }}>
                <span className="card-title">Activity ({activities.length})</span>
                <button className="btn btn-ghost" style={{ fontSize: 12 }} onClick={() => setShowLog(v => !v)}>+ Log note</button>
              </div>
              {showLog && (
                <div style={{ marginBottom: 16 }}>
                  <textarea
                    className="search-input"
                    style={{ width: '100%', resize: 'vertical', minHeight: 80, fontFamily: 'var(--font-body)', lineHeight: 1.5 }}
                    placeholder="Add a note..."
                    value={note}
                    onChange={e => setNote(e.target.value)}
                    autoFocus
                  />
                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    <button className="btn btn-primary" style={{ fontSize: 12 }} onClick={logNote}>Save note</button>
                    <button className="btn btn-ghost" style={{ fontSize: 12 }} onClick={() => { setShowLog(false); setNote(''); }}>Cancel</button>
                  </div>
                </div>
              )}
              <div style={{ position: 'relative', paddingLeft: 24 }}>
                <div style={{ position: 'absolute', left: 7, top: 0, bottom: 0, width: 2, background: 'var(--border)' }} />
                {activities.map(a => (
                  <div key={a.id} style={{ position: 'relative', marginBottom: 20 }}>
                    <div style={{ position: 'absolute', left: -24, top: 2, width: 14, height: 14, borderRadius: '50%', background: 'var(--bg-input)', border: '2px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 8 }}>
                      {activityIcon(a.type)}
                    </div>
                    <div style={{ background: 'var(--bg-input)', borderRadius: 8, padding: '10px 14px' }}>
                      <div style={{ fontSize: 13, color: 'var(--text-primary)', marginBottom: 4 }}>{a.text}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>{a.actor} · {a.date}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right sidebar */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Quick stats */}
          <div className="card">
            <div className="card-title" style={{ marginBottom: 14 }}>Summary</div>
            {[
              ['Open deals', contactDeals.filter(d => !['Activated','Onboarded'].includes(d.stage)).length],
              ['Total deal value', `$${contactDeals.reduce((s,d) => s+d.amount, 0).toLocaleString()}`],
              ['Open tasks', contactTasks.filter(t => !t.done).length],
              ['Member since', contact.created],
            ].map(([label, value]) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', padding: '9px 0', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                <span style={{ color: 'var(--text-muted)' }}>{label}</span>
                <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{value}</span>
              </div>
            ))}
          </div>

          {/* Contact info */}
          <div className="card">
            <div className="card-title" style={{ marginBottom: 14 }}>Contact info</div>
            {[
              ['Email', contact.email],
              ['Phone', contact.phone || '—'],
              ['Source', contact.source],
            ].map(([label, value]) => (
              <div key={label} style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, color: 'var(--text-dim)', fontFamily: 'var(--font-ui)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: 2 }}>{label}</div>
                <div style={{ fontSize: 12, color: 'var(--text-primary)', wordBreak: 'break-all' }}>{value}</div>
              </div>
            ))}
          </div>

          {/* Quick actions */}
          <div className="card">
            <div className="card-title" style={{ marginBottom: 14 }}>Quick actions</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {[
                ['✉ Send email', 'Email composer opening...'],
                ['📋 Create task', 'Task created'],
                ['💼 Create deal', 'Deal created'],
                ['📞 Log call', 'Call logged'],
              ].map(([label, msg]) => (
                <button key={label} className="btn btn-ghost" style={{ width: '100%', justifyContent: 'flex-start', fontSize: 12 }} onClick={() => toast?.(msg)}>
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
